import dayjs from 'dayjs';
import { JSONContent } from '@tiptap/react';
import { parseDescriptionJson, richTextHasContent } from './richTextDescription';
import { DatePickerInput } from '@mantine/dates';
import styles from './TasksManagement.module.css';
import { ProviderRole } from '@bariendo/lib/constants';
import { ClinicalTaskEditFields } from '@bariendo/lib/clinical-tasks';
import { CreateClinicalTaskInput } from '@bariendo/lib/task-management';
import { RichTextInput } from '@/components/RichTextInput/RichTextInput';
import { RoleAvatar, TeammateAvatar } from '@/components/TeamAvatar';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { usePractitionersWithRoles } from '@/hooks/useProviderDirectory';
import { createReference, formatHumanName, getReferenceString } from '@medplum/core';
import { Practitioner, Reference } from '@medplum/fhirtypes';
import { ReactElement, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IconCalendar, IconChevronRight, IconFlag } from '@tabler/icons-react';
import { Box, Button, Flex, Group, Select, Text, TextInput, UnstyledButton } from '@mantine/core';
import { TASK_PERFORMER_SELECT_DATA, TASK_PRIORITY_SELECT_DATA, TASK_TAGGABLE_ROLES } from '@/services/task-management';

export interface TaskFormProps {
  /** Prefill for edit mode; omit for a blank create form. */
  initialValues?: ClinicalTaskEditFields;
  loading: boolean;
  submitLabel: string;
  /**
   * Extra precondition from the caller, ANDed with this form's own validity.
   * The inbox uses it to hold Save until a patient is chosen — a field this
   * form does not own because the patient timeline supplies it implicitly.
   */
  submitDisabled?: boolean;
  /**
   * Caller-supplied field rendered as the form's first row, INSIDE the form
   * element. Inside matters: the Escape / Cmd+Enter shortcuts are bound to
   * this form's node, so a field rendered outside it would silently lose
   * them. The inbox puts its patient picker here.
   */
  headerSlot?: ReactNode;
  onSubmit: (fields: ClinicalTaskEditFields) => void | Promise<void>;
  onCancel: () => void;
}

// parseDescriptionJson / richTextHasContent live in richTextDescription.ts —
// exporting non-components from this file breaks Vite fast refresh.

/**
 * A new task starts assigned to whoever is writing it (BAR-2678): the team
 * writes tasks for themselves far more often than for another team, so the
 * role default made most creations a two-step. Edit mode leaves the existing
 * assignment alone — reopening someone else's task must never quietly move it.
 */
function initialAssigneeValue(
  initialValues: ClinicalTaskEditFields | undefined,
  currentUser: Reference<Practitioner> | undefined
): string {
  if (initialValues) {
    return initialValues.assignee?.reference ?? initialValues.performer ?? ProviderRole.PATIENT_COORDINATOR;
  }
  return currentUser?.reference ?? ProviderRole.PATIENT_COORDINATOR;
}

/** The inline task form, shared by create and edit (the mockup renders both identically). */
export function TaskForm({
  initialValues,
  loading,
  submitLabel,
  submitDisabled = false,
  headerSlot,
  onSubmit,
  onCancel,
}: TaskFormProps): ReactElement {
  const formRef = useRef<HTMLDivElement>(null);
  const { currentUser } = useCurrentUser();
  const currentUserRef = useMemo(() => (currentUser?.id ? createReference(currentUser) : undefined), [currentUser]);

  const [title, setTitle] = useState(initialValues?.title ?? '');
  // One value for the grouped assignee select: either a role code or a
  // practitioner reference string ('Practitioner/…' — never hand-built).
  const [assigneeValue, setAssigneeValue] = useState<string | null>(() =>
    initialAssigneeValue(initialValues, currentUserRef)
  );
  const [priority, setPriority] = useState<CreateClinicalTaskInput['priority']>(initialValues?.priority ?? 'routine');
  const [dueDate, setDueDate] = useState<Date | null>(
    initialValues?.dueDate ? dayjs(initialValues.dueDate).toDate() : null
  );
  const [descriptionExpanded, setDescriptionExpanded] = useState(Boolean(initialValues?.description));
  const [descriptionJson, setDescriptionJson] = useState<JSONContent | object>(
    parseDescriptionJson(initialValues?.description)
  );

  const practitioners = usePractitionersWithRoles(TASK_TAGGABLE_ROLES);

  // Practitioner references keyed by their reference string. Includes the
  // task's current assignee even before the directory loads, so an edit
  // form never shows an empty select or loses the assignment on save.
  const teamByValue = useMemo(() => {
    const map = new Map<string, { reference: Reference<Practitioner>; label: string; practitioner?: Practitioner }>();
    if (initialValues?.assignee?.reference) {
      map.set(initialValues.assignee.reference, {
        reference: initialValues.assignee,
        label: initialValues.assignee.display ?? initialValues.assignee.reference,
      });
    }
    // The creator, for the same reason: they are the create-mode default, and
    // the roster loads a beat later than the form opens. Without this the
    // select would sit blank on first paint — and for anyone off the taggable
    // roster it would stay blank.
    if (currentUserRef?.reference) {
      map.set(currentUserRef.reference, {
        reference: currentUserRef,
        label: formatHumanName(currentUser?.name?.[0]) || currentUserRef.reference,
        practitioner: currentUser,
      });
    }
    for (const practitioner of practitioners) {
      const reference = createReference(practitioner);
      const value = getReferenceString(reference);
      if (value) {
        map.set(value, { reference, label: formatHumanName(practitioner.name?.[0]) || value, practitioner });
      }
    }
    return map;
  }, [practitioners, initialValues?.assignee, currentUser, currentUserRef]);

  const selectData = useMemo(
    () => [
      { group: 'Roles', items: [...TASK_PERFORMER_SELECT_DATA] },
      {
        group: 'Individual',
        items: [...teamByValue.entries()]
          .map(([value, entry]) => ({ value, label: entry.label }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      },
    ],
    [teamByValue]
  );

  const renderAssigneeAvatar = (value: string | null, size: number): ReactElement | undefined => {
    if (!value) {
      return undefined;
    }
    const entry = teamByValue.get(value);
    if (entry) {
      return <TeammateAvatar practitioner={entry.practitioner ?? entry.reference} size={size} />;
    }
    return <RoleAvatar role={value as ProviderRole} size={size} />;
  };
  const canSave = title.trim().length > 0 && assigneeValue !== null && !loading && !submitDisabled;

  const handleSave = useCallback(async () => {
    if (!canSave || !assigneeValue) {
      return;
    }

    const description = richTextHasContent(descriptionJson) ? JSON.stringify(descriptionJson) : undefined;
    const teamEntry = teamByValue.get(assigneeValue);

    await onSubmit({
      title: title.trim(),
      performer: teamEntry ? undefined : (assigneeValue as ProviderRole),
      assignee: teamEntry?.reference,
      priority,
      description,
      // Plain date, not an instant: every viewer parses YYYY-MM-DD as their
      // own local day, so the calendar day never shifts across timezones.
      dueDate: dueDate ? dayjs(dueDate).format('YYYY-MM-DD') : undefined,
    });
  }, [assigneeValue, canSave, descriptionJson, dueDate, onSubmit, priority, teamByValue, title]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCancel();
        return;
      }

      if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        void handleSave();
      }

      if (event.key === 's' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        void handleSave();
      }
    };

    const node = formRef.current;
    node?.addEventListener('keydown', handleKeyDown);
    return () => node?.removeEventListener('keydown', handleKeyDown);
  }, [handleSave, onCancel]);

  return (
    <Box ref={formRef} className={styles.createTaskForm} p="sm" mb="xs">
      {headerSlot && <Box mb="xs">{headerSlot}</Box>}
      <Flex className={styles.createTaskTop} align="center" gap="xs" mb="xs">
        {renderAssigneeAvatar(assigneeValue, 30) ?? <Box className={styles.roleAvatar} aria-hidden />}
        <TextInput
          flex={1}
          variant="unstyled"
          placeholder="Task title..."
          value={title}
          onChange={(event) => setTitle(event.currentTarget.value)}
          disabled={loading}
          styles={{
            input: {
              fontSize: 14,
              fontWeight: 500,
            },
          }}
        />
      </Flex>

      <Group gap="xs" mb="xs" wrap="nowrap">
        {/* Due dates are DATES, not timestamps — day granularity everywhere. */}
        {/* TODO: Add pre-defined due date options (today, tomorrow, this weekend, and next week) */}
        <DatePickerInput
          size="xs"
          clearable
          value={dueDate}
          disabled={loading}
          placeholder="Due date"
          valueFormat="ddd MMM D"
          className={styles.pillField}
          leftSection={<IconCalendar size={12} />}
          minDate={dayjs().startOf('day').toDate()}
          onChange={(value) => setDueDate(value ? dayjs(value).toDate() : null)}
        />

        <Select
          size="xs"
          disabled={loading}
          allowDeselect={false}
          className={styles.pillField}
          value={priority ?? 'routine'}
          data={[...TASK_PRIORITY_SELECT_DATA]}
          onChange={(value) => setPriority((value as CreateClinicalTaskInput['priority']) ?? 'routine')}
          leftSection={
            <IconFlag
              size={12}
              color={priority === 'high' ? 'var(--mantine-color-error)' : 'var(--mantine-color-gray-5)'}
            />
          }
        />
      </Group>

      <Select
        size="xs"
        searchable
        value={assigneeValue}
        disabled={loading}
        allowDeselect={false}
        className={styles.pillField}
        data={selectData}
        onChange={(value) => setAssigneeValue(value)}
        leftSection={renderAssigneeAvatar(assigneeValue, 18)}
        renderOption={({ option }) => (
          <Group gap="xs" wrap="nowrap">
            {renderAssigneeAvatar(option.value, 18)}
            <Text size="xs">{option.label}</Text>
          </Group>
        )}
      />

      <Box className={styles.descriptionSection} my="sm">
        {descriptionExpanded ? (
          <RichTextInput
            disabled={loading}
            value={descriptionJson}
            contentMinHeight="120px"
            contentMaxHeight="160px"
            placeholder="Add description"
            onChange={(_text, json) => setDescriptionJson(json)}
            styles={{
              root: {
                zoom: 0.8,
              },
            }}
          />
        ) : (
          <UnstyledButton
            className={styles.descriptionToggle}
            onClick={() => setDescriptionExpanded(true)}
            disabled={loading}
          >
            <IconChevronRight size={12} style={{ opacity: 0.55 }} />
            <Text size="sm" c="dimmed">
              Add description
            </Text>
          </UnstyledButton>
        )}
      </Box>

      <Group className={styles.formFooter} justify="flex-end" gap="xs">
        <Button variant="default" bdrs="md" size="xs" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button
          size="xs"
          bdrs="md"
          bg="var(--darkGreen)"
          loading={loading}
          disabled={!canSave}
          onClick={() => void handleSave()}
        >
          {submitLabel}
        </Button>
      </Group>
    </Box>
  );
}
